import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import TouchAppIcon from '@mui/icons-material/TouchApp';

interface LEDCode {
  description: string;
  greenLed: string;
  redLed: string;
  actions: string[];
}

interface ButtonAction {
  duration: string;
  feedback: string;
  action: string;
}

const buttonActions: ButtonAction[] = [
  {
    duration: "< 2 secondes",
    feedback: "-",
    action: "Aucune action (appui trop court)"
  },
  {
    duration: "2 à 5 secondes",
    feedback: "3 clignotements verts",
    action: "Envoie un diagnostic immédiat"
  },
  {
    duration: "5 à 10 secondes",
    feedback: "2 clignotements verts",
    action: "Lance une mesure des capteurs"
  },
  {
    duration: "10 à 15 secondes",
    feedback: "LED rouge clignotante lente",
    action: "Coupe tous les relais (arrêt d'urgence)"
  },
  {
    duration: "> 15 secondes",
    feedback: "LED rouge clignotante rapide",
    action: "Redémarre le Raspberry Pi"
  }
];

// États normaux (LED Verte)
const normalStates: LEDCode[] = [
  {
    description: "Démarrage du boîtier",
    greenLed: 'Clignotant rapide',
    redLed: 'Éteinte',
    actions: ["Attendre la fin de l'initialisation (quelques secondes)"]
  },
  {
    description: "Connexion en cours",
    greenLed: 'Alternance avec rouge',
    redLed: 'Alternance avec verte',
    actions: ["Vérifier WiFi puis MQTT"]
  },
  {
    description: "Fonctionnement normal",
    greenLed: 'Flash bref toutes les 5s',
    redLed: 'Éteinte',
    actions: ["Aucune action requise"]
  },
  {
    description: "Lecture des capteurs",
    greenLed: 'Allumée fixe 3s',
    redLed: 'Éteinte',
    actions: ["Aucune action requise"]
  },
];

// Codes d'erreur regroupés (LED Rouge - pulses répétés)
const errorCodes: LEDCode[] = [
  {
    description: "Erreur WiFi",
    greenLed: 'Éteinte',
    redLed: '1 pulse + pause',
    actions: ["Vérifier SSID/mot de passe dans device_config.json", "Vérifier la portée du réseau WiFi", "Redémarrer le routeur si nécessaire"]
  },
  {
    description: "Erreur MQTT",
    greenLed: 'Éteinte',
    redLed: '2 pulses + pause',
    actions: ["Vérifier IP/port broker (rc=4)", "Vérifier username/password (rc=5)", "Vérifier que Mosquitto est actif sur le serveur"]
  },
  {
    description: "Erreur Ports/Capteurs",
    greenLed: 'Éteinte',
    redLed: '3 pulses + pause',
    actions: ["Vérifier câblage RS485", "Vérifier l'intégrité des capteurs", "Vérifier adresses Modbus"]
  },
  {
    description: "Erreur Système",
    greenLed: 'Éteinte',
    redLed: '4 pulses + pause',
    actions: ["Redémarrer le boîtier", "Vérifier espace disque (df -h)", "Consulter journalctl -u mbp-v2 -n 50"]
  },
  {
    description: "Erreur Inconnue",
    greenLed: 'Éteinte',
    redLed: '5 pulses + pause',
    actions: ["Consulter /var/log/mbp-v2.log", "Envoyer diagnostic: bouton 2-5s", "Contacter le support technique"]
  },
];

const ledCodes: LEDCode[] = [...normalStates, ...errorCodes];

const LEDCodesTable: React.FC = () => {
  return (
    <Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircleIcon sx={{ color: '#4caf50', fontSize: 12 }} />
                  LED Verte
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircleIcon sx={{ color: '#f44336', fontSize: 12 }} />
                  LED Rouge
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ledCodes.map((code, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body2">{code.description}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{code.greenLed}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{code.redLed}</Typography>
                </TableCell>
                <TableCell>
                  <List dense disablePadding sx={{ '& .MuiListItem-root': { py: 0, px: 0 } }}>
                    {code.actions.map((action, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText 
                          primary={`• ${action}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <strong>LEDs Relais :</strong> Chaque relais dispose d'une LED dédiée qui s'allume quand le relais est actif (ON).
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Section Bouton */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TouchAppIcon color="primary" />
        <Typography variant="h6">Bouton utilisateur</Typography>
      </Box>
      
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Durée d'appui</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Retour visuel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action déclenchée</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buttonActions.map((btn, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{btn.duration}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{btn.feedback}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{btn.action}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LEDCodesTable;
